import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { logEvent } from '../utils/analytics';
import { trackFirstChat } from '../utils/metaPixel';
import './Dashboard.css';
import '../components/HeroSection.css'; // For hero section styles

const API_BASE_URL = '/api';

function Dashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const [activeMenu, setActiveMenu] = useState('home');
  
  // Chat states
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState([]);
  const [isThinking, setIsThinking] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [chatTitle, setChatTitle] = useState('New Chat');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [currentChatId, setCurrentChatId] = useState(null);
  const [chats, setChats] = useState([]);
  const [isLoadingChats, setIsLoadingChats] = useState(false);
  const [shareToken, setShareToken] = useState(null); // Store share token
  const [isSharing, setIsSharing] = useState(false); // Track sharing state
  const [showBookFilterPopup, setShowBookFilterPopup] = useState(false); // Book filter popup visibility
  const [books, setBooks] = useState([]); // All books from database
  const [filteredBooks, setFilteredBooks] = useState([]); // Filtered books based on search
  const [selectedBooks, setSelectedBooks] = useState([]); // Selected book IDs
  const [bookSearchQuery, setBookSearchQuery] = useState(''); // Search query for books
  const [isLoadingBooks, setIsLoadingBooks] = useState(false); // Loading state for books
  
  const [showExamFilterPopup, setShowExamFilterPopup] = useState(false); // Exam filter popup visibility
  const [exams, setExams] = useState([]); // All exams from database
  const [filteredExams, setFilteredExams] = useState([]); // Filtered exams based on search
  const [selectedExams, setSelectedExams] = useState([]); // Selected exam IDs
  const [examSearchQuery, setExamSearchQuery] = useState(''); // Search query for exams
  const [isLoadingExams, setIsLoadingExams] = useState(false); // Loading state for exams

  // Message feedback states
  const [messageFeedback, setMessageFeedback] = useState({}); // {messageIndex: 'like' | 'dislike'}
  
  const [showSubjectFilterPopup, setShowSubjectFilterPopup] = useState(false); // Subject filter popup visibility
  const [subSpecialities, setSubSpecialities] = useState([]); // All sub-specialities from database
  const [filteredSubSpecialities, setFilteredSubSpecialities] = useState([]); // Filtered sub-specialities based on search
  const [selectedSubSpecialities, setSelectedSubSpecialities] = useState([]); // Selected sub-speciality IDs
  const [subjectSearchQuery, setSubjectSearchQuery] = useState(''); // Search query for sub-specialities
  const [isLoadingSubSpecialities, setIsLoadingSubSpecialities] = useState(false); // Loading state for sub-specialities
  const [showFilterMenu, setShowFilterMenu] = useState(false); // Filter menu visibility

  // Citation tooltip state
  const [showCitationTooltip, setShowCitationTooltip] = useState(false);
  const [tooltipContent, setTooltipContent] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

  const messagesEndRef = useRef(null);
  const lastAssistantMessageRef = useRef(null);

  useEffect(() => {
    checkSession();
  }, []);

  useEffect(() => {
    const path = location.pathname;
    if (path.includes('/chat')) {
      setActiveMenu('ai-chat');
      // Extract chatId from URL if present
      const chatIdMatch = path.match(/\/chat\/(\d+)/);
      if (chatIdMatch) {
        setCurrentChatId(parseInt(chatIdMatch[1]));
      } else {
        setCurrentChatId(null);
        setMessages([]);
        setChatTitle('New Chat');
      }
    } else if (path.includes('/exams')) {
      setActiveMenu('exams');
    } else if (path.includes('/profile')) {
      setActiveMenu('profile');
    } else {
      setActiveMenu('home');
    }
  }, [location.pathname]);

  // Load chats when user is authenticated and on chat page
  useEffect(() => {
    if (userData && (userData.user_type === 'medical_student' || userData.user_type === 'doctor') && location.pathname.includes('/chat')) {
      loadChats();
    }
  }, [userData, location.pathname]);

  // Load messages when currentChatId changes (only if userData is ready)
  useEffect(() => {
    if (currentChatId && userData) {
      loadMessages(currentChatId);
      loadChatFeedback(currentChatId);
    } else if (!currentChatId) {
      setMessages([]);
      setChatTitle('New Chat');
      setMessageFeedback({}); // Clear feedback for new chat
      // Clear all filters on new chat
      setSelectedBooks([]);
      setSelectedExams([]);
      setSelectedSubSpecialities([]);
    }
  }, [currentChatId, userData]);

  useEffect(() => {
    // During streaming, scroll to bottom to show new tokens
    if (isStreaming || isThinking) {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    }
    // After streaming completes, scroll to top of the last assistant message
    else if (lastAssistantMessageRef.current && messages.length > 0) {
      const lastMessage = messages[messages.length - 1];
      if (lastMessage.role === 'assistant') {
        lastAssistantMessageRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }, [messages, isThinking, isStreaming]);

  // Close filter menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showFilterMenu && !event.target.closest('.filter-btn-wrapper')) {
        setShowFilterMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showFilterMenu]);

  const checkSession = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/doctor-auth/check-session`, {
        credentials: 'include'
      });
      const data = await response.json();

      if (data.authenticated && data.data.doctor) {
        setUserData(data.data.doctor);
      } else {
        navigate('/login');
      }
    } catch (error) {
      console.error('Session check error:', error);
      navigate('/login');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API_BASE_URL}/doctor-auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (error) {
      console.error('Logout API error:', error);
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('doctorId');
      localStorage.removeItem('userEmail');
      navigate('/login');
    }
  };

  const handleMenuClick = (menuItem) => {
    setActiveMenu(menuItem);
    if (menuItem === 'home') {
      // For doctors, home = AI chat; for students, home = dashboard
      if (userData?.user_type === 'doctor') {
        navigate('/chat');
      } else {
        navigate('/dashboard');
      }
    } else if (menuItem === 'ai-chat') {
      navigate('/chat');
    } else if (menuItem === 'exams') {
      navigate('/exams');
    } else if (menuItem === 'question-bank') {
      navigate('/question-bank');
    } else if (menuItem === 'feedback') {
      navigate('/feedback');
    } else if (menuItem === 'profile') {
      navigate('/edit-profile');
    }
  };

  const loadChats = async () => {
    try {
      setIsLoadingChats(true);
      const endpoint = userData?.user_type === 'doctor' ? '/chats' : '/student-chats';
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success) {
        setChats(data.chats || []);
      }
    } catch (error) {
      console.error('Error loading chats:', error);
    } finally {
      setIsLoadingChats(false);
    }
  };

  const loadMessages = async (chatId) => {
    try {
      const endpoint = userData?.user_type === 'doctor' ? '/chats' : '/student-chats';
      const response = await fetch(`${API_BASE_URL}${endpoint}/${chatId}/messages`, {
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success) {
        const formattedMessages = data.messages.map(msg => ({
          role: msg.role,
          content: msg.content,
          // Images and tables removed - not displaying them
        }));
        setMessages(formattedMessages);
        // Update title from chat list or keep current
        const chat = chats.find(c => c.id === chatId);
        if (chat) {
          setChatTitle(chat.title);
        }
        // Load filters from chat (if any)
        if (userData?.user_type === 'medical_student') {
          console.log('Loading filters from chat:', data.filters); // Debug log
          if (data.filters) {
            if (data.filters.book_ids && Array.isArray(data.filters.book_ids)) {
              setSelectedBooks(data.filters.book_ids);
              console.log('Loaded book_ids:', data.filters.book_ids); // Debug log
            } else if (data.filters.book_id) {
              // Backward compatibility: single book_id
              setSelectedBooks([data.filters.book_id]);
              console.log('Loaded book_id (single):', data.filters.book_id); // Debug log
            } else {
              setSelectedBooks([]);
            }
            
            if (data.filters.exam_ids && Array.isArray(data.filters.exam_ids)) {
              setSelectedExams(data.filters.exam_ids);
              console.log('Loaded exam_ids:', data.filters.exam_ids); // Debug log
            } else {
              setSelectedExams([]);
            }
            
            if (data.filters.sub_speciality_ids && Array.isArray(data.filters.sub_speciality_ids)) {
              setSelectedSubSpecialities(data.filters.sub_speciality_ids);
              console.log('Loaded sub_speciality_ids:', data.filters.sub_speciality_ids); // Debug log
            } else {
              setSelectedSubSpecialities([]);
            }
          } else {
            // Clear filters if no filters in chat
            setSelectedBooks([]);
            setSelectedExams([]);
            setSelectedSubSpecialities([]);
          }
        } else if (userData?.user_type === 'doctor') {
          // Load book filters for doctors
          console.log('Loading filters from chat for doctor:', data.filter_book_ids); // Debug log
          if (data.filter_book_ids && Array.isArray(data.filter_book_ids)) {
            setSelectedBooks(data.filter_book_ids);
            console.log('Loaded book_ids for doctor:', data.filter_book_ids); // Debug log
          } else {
            setSelectedBooks([]);
          }
          // Doctors don't use exam/sub-speciality filters
          setSelectedExams([]);
          setSelectedSubSpecialities([]);
        } else {
          // Clear filters for unknown user types
          setSelectedBooks([]);
          setSelectedExams([]);
          setSelectedSubSpecialities([]);
        }
      }
    } catch (error) {
      console.error('Error loading messages:', error);
    }
  };

  const handleNewChat = async () => {
    try {
      const endpoint = userData?.user_type === 'doctor' ? '/chats' : '/student-chats';
      const requestBody = userData?.user_type === 'doctor' 
        ? { title: 'New Chat', specialty: userData?.specialty || '' } // Use doctor's specialty
        : { title: 'New Chat' };
      
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(requestBody)
      });
      const data = await response.json();
      if (data.success) {
        setCurrentChatId(data.chat.id);
        navigate(`/chat/${data.chat.id}`);
    setMessages([]);
        setChatTitle('New Chat');
        setShareToken(null); // Reset share token
        // Clear all filters on new chat
        setSelectedBooks([]);
        setSelectedExams([]);
        setSelectedSubSpecialities([]);
        loadChats(); // Refresh chat list
      }
    } catch (error) {
      console.error('Error creating new chat:', error);
    }
  };

  const handleShare = async () => {
    if (!currentChatId) {
      alert('No chat selected');
      return;
    }

    setIsSharing(true);
    try {
      const endpoint = userData?.user_type === 'doctor' ? '/chats' : '/student-chats';
      const response = await fetch(`${API_BASE_URL}${endpoint}/${currentChatId}/share`, {
        method: 'POST',
        credentials: 'include'
      });

      const data = await response.json();

      if (data.success && data.shareToken) {
        setShareToken(data.shareToken);

        // Generate share URL
        const shareUrl = `${window.location.origin}/shared/${data.shareToken}`;

        // Track chat share event
        logEvent('Chat', 'chat_shared', {
          chat_id: currentChatId,
          share_token_generated: true,
          message_count: messages.length,
        });

        // Copy to clipboard
        try {
          await navigator.clipboard.writeText(shareUrl);
          alert('Chat link copied to clipboard!');
        } catch (err) {
          // Fallback: show the URL in a prompt
          prompt('Copy this link to share:', shareUrl);
        }
      } else {
        alert('Failed to generate share link');
      }
    } catch (error) {
      console.error('Error sharing chat:', error);
      alert('Failed to share chat. Please try again.');
    } finally {
      setIsSharing(false);
    }
  };

  const loadBooks = async () => {
    setIsLoadingBooks(true);
    try {
      let url = `${API_BASE_URL}/books?limit=1000&status=completed`;
      
      // For students: filter by country_studying
      if (userData?.user_type === 'medical_student') {
        const country = userData?.country_studying || '';
        if (country) {
          url += `&country=${encodeURIComponent(country)}`;
        }
      }
      
      // For doctors: filter by specialty and country_practicing
      if (userData?.user_type === 'doctor') {
        const specialty = userData?.specialty || '';
        const country = userData?.country_practicing || '';
        if (specialty) {
          url += `&specialty=${encodeURIComponent(specialty)}`;
        }
        if (country) {
          url += `&country=${encodeURIComponent(country)}`;
        }
      }
      
      const response = await fetch(url, {
        credentials: 'include'
      });
      const data = await response.json();
      console.log('Books API response:', data); // Debug log
      if (data.success && data.data) {
        setBooks(data.data);
        setFilteredBooks(data.data);
        console.log('Loaded books:', data.data.length); // Debug log
      } else {
        console.error('Books API error:', data);
      }
    } catch (error) {
      console.error('Error loading books:', error);
    } finally {
      setIsLoadingBooks(false);
    }
  };

  const handleBookSearch = (query) => {
    setBookSearchQuery(query);
    if (!query.trim()) {
      setFilteredBooks(books);
      return;
    }
    const filtered = books.filter(book => {
      const searchLower = query.toLowerCase();
      const nameMatch = book.name?.toLowerCase().includes(searchLower);
      const authorMatch = book.author?.toLowerCase().includes(searchLower);
      return nameMatch || authorMatch;
    });
    setFilteredBooks(filtered);
  };

  const handleBookToggle = (bookId) => {
    setSelectedBooks(prev => {
      if (prev.includes(bookId)) {
        return prev.filter(id => id !== bookId);
      } else {
        return [...prev, bookId];
      }
    });
  };

  const handleClearAllBooks = () => {
    setSelectedBooks([]);
  };

  const handleApplyBookFilter = async () => {
    setShowBookFilterPopup(false);

    // Track book filter applied
    logEvent('Chat', 'book_filter_applied', {
      chat_id: currentChatId,
      book_count: selectedBooks.length,
      user_type: userData?.user_type,
    });

    // Save filters to database if chat exists
    if (currentChatId) {
      try {
        console.log('Saving book filters:', selectedBooks); // Debug log
        const endpoint = userData?.user_type === 'doctor' ? '/chats' : '/student-chats';
        const requestBody = userData?.user_type === 'doctor'
          ? { 
              message: '', // Empty message just to update filters
              book_ids: selectedBooks.length > 0 ? selectedBooks : null
            }
          : { 
              message: '', // Empty message just to update filters
              book_ids: selectedBooks.length > 0 ? selectedBooks : null
            };
        
        const response = await fetch(`${API_BASE_URL}${endpoint}/${currentChatId}/messages/stream`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          credentials: 'include',
          body: JSON.stringify(requestBody)
        });
        const data = await response.json();
        console.log('Filter save response:', data); // Debug log
      } catch (error) {
        console.error('Error saving book filters:', error);
      }
    }
  };

  const loadExams = async () => {
    setIsLoadingExams(true);
    try {
      const country = userData?.country_studying || '';
      const url = country 
        ? `${API_BASE_URL}/exams?limit=1000&country=${encodeURIComponent(country)}`
        : `${API_BASE_URL}/exams?limit=1000`;
      const response = await fetch(url, {
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success && data.data) {
        setExams(data.data);
        setFilteredExams(data.data);
      }
    } catch (error) {
      console.error('Error loading exams:', error);
    } finally {
      setIsLoadingExams(false);
    }
  };

  const handleExamSearch = (query) => {
    setExamSearchQuery(query);
    if (!query.trim()) {
      setFilteredExams(exams);
      return;
    }
    const filtered = exams.filter(exam => {
      const searchLower = query.toLowerCase();
      return exam.name?.toLowerCase().includes(searchLower);
    });
    setFilteredExams(filtered);
  };

  const handleExamToggle = (examId) => {
    setSelectedExams(prev => {
      if (prev.includes(examId)) {
        return prev.filter(id => id !== examId);
      } else {
        return [...prev, examId];
      }
    });
  };

  const handleClearAllExams = () => {
    setSelectedExams([]);
  };

  const handleApplyExamFilter = async () => {
    setShowExamFilterPopup(false);

    // Track exam filter applied
    logEvent('Chat', 'exam_filter_applied', {
      chat_id: currentChatId,
      exam_count: selectedExams.length,
      user_type: userData?.user_type,
    });

    // Save filters to database if chat exists (only for students)
    if (currentChatId && userData?.user_type === 'medical_student') {
      try {
        console.log('Saving exam filters:', selectedExams); // Debug log
        const response = await fetch(`${API_BASE_URL}/student-chats/${currentChatId}/messages/stream`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          credentials: 'include',
          body: JSON.stringify({
            message: '', // Empty message just to update filters
            exam_ids: selectedExams.length > 0 ? selectedExams : null
          })
        });
        const data = await response.json();
        console.log('Filter save response:', data); // Debug log
      } catch (error) {
        console.error('Error saving exam filters:', error);
      }
    }
  };

  const loadSubSpecialities = async () => {
    setIsLoadingSubSpecialities(true);
    try {
      const country = userData?.country_studying || '';
      const url = country 
        ? `${API_BASE_URL}/sub-specialities?limit=1000&country=${encodeURIComponent(country)}`
        : `${API_BASE_URL}/sub-specialities?limit=1000`;
      const response = await fetch(url, {
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success && data.data) {
        setSubSpecialities(data.data);
        setFilteredSubSpecialities(data.data);
      }
    } catch (error) {
      console.error('Error loading sub-specialities:', error);
    } finally {
      setIsLoadingSubSpecialities(false);
    }
  };

  const handleSubjectSearch = (query) => {
    setSubjectSearchQuery(query);
    if (!query.trim()) {
      setFilteredSubSpecialities(subSpecialities);
      return;
    }
    const filtered = subSpecialities.filter(sub => {
      const searchLower = query.toLowerCase();
      return sub.name?.toLowerCase().includes(searchLower);
    });
    setFilteredSubSpecialities(filtered);
  };

  const handleSubjectToggle = (subSpecialityId) => {
    setSelectedSubSpecialities(prev => {
      if (prev.includes(subSpecialityId)) {
        return prev.filter(id => id !== subSpecialityId);
        } else {
        return [...prev, subSpecialityId];
      }
    });
  };

  const handleClearAllSubjects = () => {
    setSelectedSubSpecialities([]);
  };

  const handleApplySubjectFilter = async () => {
    setShowSubjectFilterPopup(false);

    // Track subject filter applied
    logEvent('Chat', 'subject_filter_applied', {
      chat_id: currentChatId,
      subject_count: selectedSubSpecialities.length,
      user_type: userData?.user_type,
    });

    // Save filters to database if chat exists (only for students)
    if (currentChatId && userData?.user_type === 'medical_student') {
      try {
        console.log('Saving subject filters:', selectedSubSpecialities); // Debug log
        const response = await fetch(`${API_BASE_URL}/student-chats/${currentChatId}/messages/stream`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          credentials: 'include',
          body: JSON.stringify({
            message: '', // Empty message just to update filters
            sub_speciality_ids: selectedSubSpecialities.length > 0 ? selectedSubSpecialities : null
          })
        });
        const data = await response.json();
        console.log('Filter save response:', data); // Debug log
      } catch (error) {
        console.error('Error saving subject filters:', error);
      }
    }
  };

  const handleSend = async () => {
    if (!message.trim() || isThinking) return;

    // Track first chat event if this is the first message
    const isFirstMessage = messages.length === 0;
    if (isFirstMessage) {
      trackFirstChat();
    }

    let chatIdToUse = currentChatId;

    // If no current chat, create one first
    if (!chatIdToUse) {
      try {
        await handleNewChat();
        // Wait a bit for chat to be created
        await new Promise(resolve => setTimeout(resolve, 500));
        // Get the newly created chat ID
        const endpoint = userData?.user_type === 'doctor' ? '/chats' : '/student-chats';
        const response = await fetch(`${API_BASE_URL}${endpoint}`, {
          credentials: 'include'
        });
        const data = await response.json();
        if (data.success && data.chats.length > 0) {
          const newChat = data.chats[0];
          chatIdToUse = newChat.id;
          setCurrentChatId(newChat.id);
          navigate(`/chat/${newChat.id}`);
          
          // Save filters to the newly created chat if any are selected (only for students)
          if (userData?.user_type === 'medical_student' && (selectedBooks.length > 0 || selectedExams.length > 0 || selectedSubSpecialities.length > 0)) {
            try {
              await fetch(`${API_BASE_URL}/student-chats/${chatIdToUse}/messages/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
                body: JSON.stringify({ 
                  message: '', // Empty message just to update filters
                  book_ids: selectedBooks.length > 0 ? selectedBooks : null,
                  exam_ids: selectedExams.length > 0 ? selectedExams : null,
                  sub_speciality_ids: selectedSubSpecialities.length > 0 ? selectedSubSpecialities : null
                })
              });
            } catch (error) {
              console.error('Error saving filters to new chat:', error);
            }
          }
        } else {
          console.error('Failed to create or retrieve chat');
          return; // Failed to create chat
        }
      } catch (error) {
        console.error('Error creating new chat:', error);
        return;
      }
    }

    if (!chatIdToUse) {
      console.error('No chat ID available');
      return;
    }

    const userMessage = message.trim();

    // Track message sent event
    logEvent('Chat', 'message_sent', {
      chat_id: chatIdToUse,
      message_length: userMessage.length,
      has_book_filters: selectedBooks.length > 0,
      has_exam_filters: selectedExams.length > 0,
      has_subject_filters: selectedSubSpecialities.length > 0,
      user_type: userData?.user_type,
    });

    setMessage('');
    setIsThinking(true);
    setIsStreaming(false);

    // Add user message to UI immediately
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);

    // Add placeholder assistant message for streaming
    let assistantMessageAdded = false;

    try {
      const endpoint = userData?.user_type === 'doctor' ? '/chats' : '/student-chats';
      const requestBody = userData?.user_type === 'doctor'
        ? {
            message: userMessage,
            book_ids: selectedBooks.length > 0 ? selectedBooks : null // Doctors can filter by books
          }
        : {
            message: userMessage,
            book_ids: selectedBooks.length > 0 ? selectedBooks : null,
            exam_ids: selectedExams.length > 0 ? selectedExams : null,
            sub_speciality_ids: selectedSubSpecialities.length > 0 ? selectedSubSpecialities : null
          };
      
      const url = `${API_BASE_URL}${endpoint}/${chatIdToUse}/messages/stream`;
      console.log('Sending message to:', url);
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Response error:', response.status, errorText);
        throw new Error(`Failed to send message: ${response.status} ${errorText}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      let assistantMessage = {
        role: 'assistant',
        content: '',
        source_citations: []
      };

      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));

              if (data.type === 'status') {
                // Status update (e.g., "Searching knowledge base...")
                // Keep thinking state during status updates
              } else if (data.type === 'title') {
                setChatTitle(data.title);
                loadChats(); // Refresh chat list with new title
              } else if (data.type === 'token') {
                // Hide thinking state when first token arrives, but set streaming to true
                if (!assistantMessageAdded) {
                  setIsThinking(false);
                  setIsStreaming(true);
                  assistantMessageAdded = true;
                  // Add assistant message to array when first token arrives
                  setMessages(prev => [...prev, { ...assistantMessage }]);
                }

                assistantMessage.content += data.content;
                // Update the last message in real-time (use functional update to avoid flicker)
                setMessages(prev => {
                  const newMessages = [...prev];
                  const lastMsg = newMessages[newMessages.length - 1];
                  if (lastMsg && lastMsg.role === 'assistant') {
                    // Create new object to trigger re-render
                    newMessages[newMessages.length - 1] = {
                      ...lastMsg,
                      content: assistantMessage.content,
                      source_citations: assistantMessage.source_citations
                    };
                  }
                  return newMessages;
                });
              } else if (data.type === 'sources') {
                // Handle sources event - convert sources object to array format
                const sourcesObj = data.sources || {};
                const citationsArray = Object.entries(sourcesObj).map(([num, source]) => ({
                  number: parseInt(num),
                  book_title: source.book_title,
                  book_author: source.book_author,
                  page_number: source.page_number,
                  text: source.text || ''
                }));
                assistantMessage.source_citations = citationsArray;

                // Update the message with sources
                setMessages(prev => {
                  const newMessages = [...prev];
                  const lastMsg = newMessages[newMessages.length - 1];
                  if (lastMsg && lastMsg.role === 'assistant') {
                    newMessages[newMessages.length - 1] = {
                      ...lastMsg,
                      source_citations: citationsArray
                    };
                  }
                  return newMessages;
                });
              } else if (data.type === 'done') {
                setIsThinking(false);
                setIsStreaming(false);
                // Ensure final message is saved
                setMessages(prev => {
                  const newMessages = [...prev];
                  const lastMsg = newMessages[newMessages.length - 1];
                  if (lastMsg && lastMsg.role === 'assistant') {
                    return newMessages;
                  }
                  return [...newMessages, { ...assistantMessage }];
                });
              } else if (data.type === 'error') {
                setIsThinking(false);
                setIsStreaming(false);
                setMessages(prev => [...prev, {
                  role: 'assistant',
                  content: `Error: ${data.message || 'An error occurred'}`
                }]);
            }
          } catch (e) {
            console.error('Error parsing SSE data:', e);
            }
          }
        }
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setIsThinking(false);
      setIsStreaming(false);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Sorry, an error occurred while processing your message. Please try again.'
      }]);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Handle message feedback (like/dislike)
  const handleMessageFeedback = async (messageIndex, feedbackType) => {
    if (!currentChatId) return;

    // Track feedback event
    logEvent('Chat', 'message_feedback_given', {
      chat_id: currentChatId,
      message_index: messageIndex,
      feedback_type: feedbackType,
    });

    try {
      const response = await fetch(
        `${API_BASE_URL}/message-feedback/chats/${currentChatId}/messages/${messageIndex}/feedback`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          credentials: 'include',
          body: JSON.stringify({ feedback_type: feedbackType })
        }
      );

      const data = await response.json();
      if (data.success) {
        // Update local state
        setMessageFeedback(prev => ({
          ...prev,
          [messageIndex]: data.feedback ? data.feedback.feedback_type : null
        }));
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
    }
  };

  // Load feedback for current chat
  const loadChatFeedback = async (chatId) => {
    if (!chatId) return;

    try {
      const response = await fetch(
        `${API_BASE_URL}/message-feedback/chats/${chatId}/feedback`,
        {
          credentials: 'include'
        }
      );

      const data = await response.json();
      if (data.success) {
        setMessageFeedback(data.data || {});
      }
    } catch (error) {
      console.error('Error loading feedback:', error);
    }
  };

  // Render message content with markdown
  const renderMessageContent = (message) => {
    if (message.role === 'user') {
      return <div>{message.content}</div>;
    }

    let content = message.content || '';

    // Process citations to add source information
    const sourceCitations = message.source_citations || [];

    // Create a map of citation numbers to source info
    const citationMap = {};
    sourceCitations.forEach(citation => {
      const num = citation.number;
      citationMap[num] = citation;
    });

    // Replace citation numbers [1], [2], [1,2] etc with styled spans
    // This needs to be done carefully to not break markdown
    const processedContent = content.replace(/\[(\d+(?:,\s*\d+)*)\]/g, (match, numbers) => {
      const nums = numbers.split(',').map(n => n.trim());
      const citationDetails = nums.map(num => {
        const citation = citationMap[num];
        if (citation) {
          return `${citation.book_title}${citation.book_author ? ' by ' + citation.book_author : ''}${citation.page_number ? ', Page ' + citation.page_number : ''}`;
        }
        return '';
      }).filter(Boolean).join(' | ');

      // Return markdown-safe citation with data-ref-num attribute (click handled via event delegation)
      const firstNum = nums[0];
      return `<sup class="inline-citation" title="${citationDetails}" data-ref-num="${firstNum}">[${numbers}]</sup>`;
    });

    // Handle click on citations via event delegation
    const handleCitationClick = (e) => {
      const target = e.target.closest('.inline-citation');
      if (target) {
        const refNum = target.getAttribute('data-ref-num');
        if (refNum) {
          const refElement = document.getElementById(`ref-${refNum}`);
          if (refElement) {
            refElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }
      }
    };

    return (
      <div className="message-content-wrapper" onClick={handleCitationClick}>
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          rehypePlugins={[rehypeRaw]}
          components={{
            // Style lists
            ul: ({ node, children, ...props }) => (
              <ul style={{ margin: '0.5rem 0', paddingLeft: '1.5rem', listStyleType: 'disc' }} {...props}>
                {children}
              </ul>
            ),
            ol: ({ node, children, ...props }) => (
              <ol style={{ margin: '0.5rem 0', paddingLeft: '1.5rem', listStyleType: 'decimal' }} {...props}>
                {children}
              </ol>
            ),
            li: ({ node, children, ...props }) => (
              <li style={{ margin: '0.15rem 0', lineHeight: '1.6', paddingLeft: '0.25rem' }} {...props}>
                {children}
              </li>
            ),
            // Style tables
            table: ({ node, children, ...props }) => (
              <div style={{ overflowX: 'auto', margin: '1rem 0' }}>
                <table style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: '0.875rem',
                  marginTop: '8px'
                }} {...props}>
                  {children}
                </table>
              </div>
            ),
            thead: ({ node, children, ...props }) => (
              <thead {...props}>{children}</thead>
            ),
            tbody: ({ node, children, ...props }) => (
              <tbody {...props}>{children}</tbody>
            ),
            tr: ({ node, children, ...props }) => (
              <tr {...props}>{children}</tr>
            ),
            th: ({ node, children, ...props }) => (
              <th style={{
                padding: '8px 12px',
                textAlign: 'left',
                backgroundColor: '#f0f0f0',
                border: '1px solid #ddd',
                fontWeight: '600',
                wordWrap: 'break-word',
                whiteSpace: 'normal'
              }} {...props}>
                {children}
              </th>
            ),
            td: ({ node, children, ...props }) => (
              <td style={{
                padding: '8px 12px',
                border: '1px solid #ddd',
                wordWrap: 'break-word',
                whiteSpace: 'normal',
                verticalAlign: 'top'
              }} {...props}>
                {children}
              </td>
            ),
            // Style images (regular markdown images only, no database images)
            img: ({ src, alt, ...props }) => {
              return (
                <img
                  src={src}
                  alt={alt}
                  {...props}
                  style={{
                    maxWidth: '100%',
                    height: 'auto',
                    borderRadius: '8px',
                    margin: '12px 0',
                    display: 'block'
                  }}
                />
              );
            },
            // Style code blocks (for tables)
            code: ({ node, inline, className, children, ...props }) => {
              const match = /language-(\w+)/.exec(className || '');
              const isTable = !inline && match && match[1] === 'table';
              
              if (isTable || (!inline && className)) {
                return (
                  <div className="message-table-container" style={{
                    margin: '12px 0',
                    padding: '12px',
                    backgroundColor: '#f9f9f9',
                    borderRadius: '8px',
                    border: '1px solid #e0e0e0',
                    overflowX: 'auto'
                  }}>
                    <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.875rem' }}>
                      <code {...props}>{children}</code>
                    </pre>
                  </div>
                );
              }
              return <code className={className} {...props}>{children}</code>;
            }
          }}
        >
          {processedContent}
        </ReactMarkdown>

        {/* Display source citations/references if available */}
        {message.source_citations && message.source_citations.length > 0 && (
          <div className="references-section-compact">
            <div className="references-header-compact">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M2 3H8C9.06087 3 10.0783 3.42143 10.8284 4.17157C11.5786 4.92172 12 5.93913 12 7V21C12 20.2044 11.6839 19.4413 11.1213 18.8787C10.5587 18.3161 9.79565 18 9 18H2V3Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M22 3H16C14.9391 3 13.9217 3.42143 13.1716 4.17157C12.4214 4.92172 12 5.93913 12 7V21C12 20.2044 12.3161 19.4413 12.8787 18.8787C13.4413 18.3161 14.2044 18 15 18H22V3Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span className="references-title-compact">For more information refer to the following literature:</span>
            </div>
            <div className="references-inline-list">
              {message.source_citations.map((source, index) => {
                const refNum = source.number || index + 1;
                return (
                  <span key={index}>
                    <a
                      href="#"
                      id={`ref-${refNum}`}
                      className="reference-link"
                      onClick={(e) => {
                        e.preventDefault();
                        if (source.text) {
                          setTooltipContent(source);
                          setTooltipPosition({ x: e.clientX, y: e.clientY });
                          setShowCitationTooltip(true);
                        }
                      }}
                      title={`${source.book_title}${source.book_author ? ' - ' + source.book_author : ''}`}
                    >
                      [{refNum}] {source.book_title}
                    </a>
                    {index < message.source_citations.length - 1 && <span className="reference-separator"> • </span>}
                  </span>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  };

  const getMenuItems = () => {
    if (!userData) return [];
    
    if (userData.user_type === 'medical_student') {
      return [
        { id: 'home', label: 'Home', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3 9L12 2L21 9V20C21 20.5304 20.7893 21.0391 20.4142 21.4142C20.0391 21.7893 19.5304 22 19 22H5C4.46957 22 3.96086 21.7893 3.58579 21.4142C3.21071 21.0391 3 20.5304 3 20V9Z" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/><path d="M9 22V12H15V22" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg> },
        { id: 'ai-chat', label: 'AI Chat', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/><path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/><path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg> },
        { id: 'exams', label: 'Exams', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M4 19V5C4 3.89543 4.89543 3 6 3H19.4C19.7314 3 20 3.26863 20 3.6V16.7143" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/><path d="M6 17L20 17" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/><path d="M6 21L20 21" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/><path d="M6 21C4.89543 21 4 20.1046 4 19C4 17.8954 4.89543 17 6 17" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/><path d="M9 7L15 7" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/></svg> },
        { id: 'question-bank', label: 'Question Bank', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M2 3H8C9.06087 3 10.0783 3.42143 10.8284 4.17157C11.5786 4.92172 12 5.93913 12 7V21C12 20.2044 11.6839 19.4413 11.1213 18.8787C10.5587 18.3161 9.79565 18 9 18H2V3Z" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/><path d="M22 3H16C14.9391 3 13.9217 3.42143 13.1716 4.17157C12.4214 4.92172 12 5.93913 12 7V21C12 20.2044 12.3161 19.4413 12.8787 18.8787C13.4413 18.3161 14.2044 18 15 18H22V3Z" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg> },
        { id: 'profile', label: 'Profile', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/><path d="M12 11C14.2091 11 16 9.20914 16 7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7C8 9.20914 9.79086 11 12 11Z" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg> }
      ];
    } else {
      return [
        { id: 'home', label: 'AI Chat', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/><path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/><path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg> },
        { id: 'feedback', label: 'Feedback', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg> },
        { id: 'profile', label: 'Profile', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/><path d="M12 11C14.2091 11 16 9.20914 16 7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7C8 9.20914 9.79086 11 12 11Z" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg> }
      ];
    }
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        fontSize: '18px',
        color: '#666'
      }}>
        Loading...
      </div>
    );
  }

  const menuItems = getMenuItems().filter(item => item.id !== 'profile');
  const isChatPage = location.pathname.includes('/chat');

  return (
    <div className="dashboard-container">
      {/* Top Header - Hide on chat page */}
      {!isChatPage && (
        <div className="dashboard-header">
          <div className="dashboard-header-left">
            <img src="/logo.svg" alt="S.C.A.L.P.E.L. Logo" className="dashboard-logo-img" />
          </div>
          <nav className="dashboard-nav desktop-nav">
            {menuItems.map((item) => (
              <button
                key={item.id}
                className={`nav-menu-item ${activeMenu === item.id ? 'active' : ''}`}
                onClick={() => handleMenuClick(item.id)}
              >
                <span className="nav-menu-icon">{item.icon}</span>
                <span className="nav-menu-label">{item.label}</span>
              </button>
            ))}
          </nav>
          <button onClick={handleLogout} className="dashboard-logout-btn">
            <svg width="20" height="20" strokeWidth="1.4" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" color="currentColor">
              <path d="M12 12H19M19 12L16 15M19 12L16 9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"></path>
              <path d="M19 6V5C19 3.89543 18.1046 3 17 3H7C5.89543 3 5 3.89543 5 5V19C5 20.1046 5.89543 21 7 21H17C18.1046 21 19 20.1046 19 19V18" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"></path>
          </svg>
            <span className="logout-text">Logout</span>
        </button>
        </div>
      )}

      {/* Chat Page */}
      {isChatPage && userData && (userData.user_type === 'medical_student' || userData.user_type === 'doctor') ? (
        <div className="chat-page-wrapper">
          {/* Mobile Menu Button */}
          <button 
            className="chat-menu-btn"
            onClick={() => setSidebarOpen(!sidebarOpen)}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M3 12H21M3 6H21M3 18H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </button>

          {/* Sidebar Overlay */}
          {sidebarOpen && (
            <div className="chat-overlay" onClick={() => setSidebarOpen(false)} />
          )}

          {/* Sidebar */}
          <aside className={`chat-sidebar ${sidebarOpen ? 'open' : ''} desktop-visible`}>
        <div className="sidebar-header">
              <img src="/logo.svg" alt="Logo" className="sidebar-logo" />
              <button className="sidebar-close" onClick={() => setSidebarOpen(false)}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </button>
        </div>
            <button className="new-chat-btn" onClick={handleNewChat}>+ New Chat</button>
        <div className="sidebar-divider"></div>
            <div className="chat-history">
              <div className="history-title">CHAT HISTORY</div>
              {isLoadingChats ? (
                <div className="history-empty">Loading...</div>
              ) : chats.length === 0 ? (
                <div className="history-empty">No chat history yet</div>
              ) : (
                <div className="chat-history-list">
                  {chats.map((chat) => (
                    <button
                  key={chat.id}
                      className={`chat-history-item ${currentChatId === chat.id ? 'active' : ''}`}
                      onClick={() => {
                    setCurrentChatId(chat.id);
                        navigate(`/chat/${chat.id}`);
                        setSidebarOpen(false);
                      }}
                    >
                      {chat.title}
                  </button>
                  ))}
        </div>
            )}
          </div>
            <div className="sidebar-menu">
              <div className="menu-title">MENU</div>
              {menuItems.map((item) => (
                <button
                  key={item.id}
                  className={`menu-item ${activeMenu === item.id ? 'active' : ''}`}
                  onClick={() => {
                    handleMenuClick(item.id);
                    setSidebarOpen(false);
                  }}
                >
                  {item.icon}
                  <span>{item.label}</span>
                </button>
              ))}
          </div>
        <div className="sidebar-footer">
              <button className="logout-btn" onClick={handleLogout}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M12 12H19M19 12L16 15M19 12L16 9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
                  <path d="M19 6V5C19 3.89543 18.1046 3 17 3H7C5.89543 3 5 3.89543 5 5V19C5 20.1046 5.89543 21 7 21H17C18.1046 21 19 20.1046 19 19V18" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
              </svg>
                Logout
            </button>
          </div>
          </aside>

          {/* Main Chat Area */}
          <main className="chat-main">
          {messages.length === 0 ? (
              <div className="chat-welcome">
                <div className="welcome-icon">
                  <img src={process.env.PUBLIC_URL + '/icon.png'} alt="AI Assistant" />
              </div>
                <h1 className="welcome-title">
                  Introducing S.C.A.L.P.E.L.:<br />
                  Simplified Clinical and Learning Platform for Evidence-based Literature
                </h1>
                <p className="welcome-text">
                  Get instant answers to medical questions, generate case studies, and explore topics with AI-powered precision.
                </p>
                <div className="welcome-input-wrapper">
                <textarea
                    className="welcome-input"
                  placeholder="Ask me anything..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  rows={4}
                    disabled={isThinking}
                  />
                  <button className="welcome-send-btn" onClick={handleSend} disabled={isThinking || !message.trim()}>
                    <svg width="18" height="18" viewBox="0 0 24 24" strokeWidth="1.4" fill="none" xmlns="http://www.w3.org/2000/svg" color="currentColor">
                      <path d="M22.1525 3.55321L11.1772 21.0044L9.50686 12.4078L2.00002 7.89795L22.1525 3.55321Z" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"></path>
                      <path d="M9.45557 12.4436L22.1524 3.55321" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"></path>
                    </svg>
                  </button>
                </div>
                {(userData?.user_type === 'medical_student' || userData?.user_type === 'doctor') && (
                  <div className="welcome-tags">
                    <button 
                      className="welcome-tag welcome-tag-primary"
                      onClick={() => {
                        setShowBookFilterPopup(true);
                        loadBooks();
                      }}
                    >
                      Ask from a Book{selectedBooks.length > 0 ? ` (${selectedBooks.length} ${selectedBooks.length === 1 ? 'book' : 'books'} selected)` : ''}
                    </button>
                    {userData?.user_type === 'medical_student' && (
                      <>
                        <button 
                          className="welcome-tag welcome-tag-primary"
                          onClick={() => {
                            setShowExamFilterPopup(true);
                            loadExams();
                          }}
                        >
                          Ask from Exam Curriculum{selectedExams.length > 0 ? ` (${selectedExams.length} ${selectedExams.length === 1 ? 'exam' : 'exams'} selected)` : ''}
                        </button>
                        <button 
                          className="welcome-tag welcome-tag-primary"
                          onClick={() => {
                            setShowSubjectFilterPopup(true);
                            loadSubSpecialities();
                          }}
                        >
                          Ask from a Subject{selectedSubSpecialities.length > 0 ? ` (${selectedSubSpecialities.length} ${selectedSubSpecialities.length === 1 ? 'subject' : 'subjects'} selected)` : ''}
                        </button>
                      </>
                    )}
              </div>
                )}
            </div>
          ) : (
              <div className="chat-conversation">
                <div className="conversation-header">
                  <h2 className="conversation-title">{chatTitle}</h2>
                  <div className="conversation-header-actions">
                    {(userData?.user_type === 'medical_student' || userData?.user_type === 'doctor') && (
                      <div className="filter-btn-wrapper">
                        <button 
                          className="filter-btn" 
                          onClick={() => {
                            // Toggle filter menu
                            setShowFilterMenu(!showFilterMenu);
                          }}
                          title={userData?.user_type === 'doctor' ? "Filter by books" : "Filter by books, exams, or subjects"}
                        >
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" strokeWidth="1.4">
                            <path d="M4 6H20M7 12H17M10 18H14" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                          {userData?.user_type === 'doctor' ? (
                            selectedBooks.length > 0 && (
                              <span className="filter-badge">
                                {selectedBooks.length}
                              </span>
                            )
                          ) : (
                            (selectedBooks.length > 0 || selectedExams.length > 0 || selectedSubSpecialities.length > 0) && (
                              <span className="filter-badge">
                                {selectedBooks.length + selectedExams.length + selectedSubSpecialities.length}
                              </span>
                            )
                          )}
                        </button>
                        {showFilterMenu && (
                          <div className="filter-menu">
                            <button 
                              className="filter-menu-item"
                              onClick={() => {
                                setShowFilterMenu(false);
                                setShowBookFilterPopup(true);
                                loadBooks();
                              }}
                            >
                              <span>Books</span>
                              {selectedBooks.length > 0 && (
                                <span className="filter-menu-count">{selectedBooks.length}</span>
                              )}
                            </button>
                            {userData?.user_type === 'medical_student' && (
                              <>
                                <button 
                                  className="filter-menu-item"
                                  onClick={() => {
                                    setShowFilterMenu(false);
                                    setShowExamFilterPopup(true);
                                    loadExams();
                                  }}
                                >
                                  <span>Exams</span>
                                  {selectedExams.length > 0 && (
                                    <span className="filter-menu-count">{selectedExams.length}</span>
                                  )}
                                </button>
                                <button 
                                  className="filter-menu-item"
                                  onClick={() => {
                                    setShowFilterMenu(false);
                                    setShowSubjectFilterPopup(true);
                                    loadSubSpecialities();
                                  }}
                                >
                                  <span>Subjects</span>
                                  {selectedSubSpecialities.length > 0 && (
                                    <span className="filter-menu-count">{selectedSubSpecialities.length}</span>
                                  )}
                                </button>
                              </>
                            )}
                                </div>
                                  )}
                                </div>
                    )}
                    <button 
                      className="share-btn" 
                      onClick={handleShare}
                      disabled={isSharing || !currentChatId}
                      title="Share chat"
                    >
                      <svg width="18" height="18" strokeWidth="1.4" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" color="#000000">
                        <path d="M20 13V19C20 20.1046 19.1046 21 18 21H6C4.89543 21 4 20.1046 4 19V13" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"></path>
                        <path d="M12 15V3M12 3L8.5 6.5M12 3L15.5 6.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"></path>
                      </svg>
                    </button>
                              </div>
                          </div>
                <div className="messages-container">
                  {messages.map((msg, idx) => {
                    const isLastAssistantMessage = msg.role === 'assistant' && idx === messages.length - 1;
                    return (
                    <div
                      key={idx}
                      className={`message ${msg.role}`}
                      ref={isLastAssistantMessage ? lastAssistantMessageRef : null}
                    >
                      {msg.role === 'assistant' && (
                        <div className="avatar">
                          <img src="/avatar.svg" alt="AI" />
                        </div>
                      )}
                      <div className={`message-bubble ${msg.role}`}>
                        {renderMessageContent(msg)}
                        {msg.role === 'assistant' && (idx < messages.length - 1 || (!isThinking && !isStreaming)) && (
                          <div className="message-feedback-section">
                            <div className="message-feedback-container">
                              <span className="feedback-prompt">Was this helpful?</span>
                              <div className="message-feedback-buttons">
                                <button
                                  className={`feedback-btn ${messageFeedback[idx] === 'like' ? 'active-like' : ''}`}
                                  onClick={() => handleMessageFeedback(idx, 'like')}
                                  title="Helpful"
                                >
                                  <svg width="20" height="20" strokeWidth="1.4" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M16.4724 20H4.1C3.76863 20 3.5 19.7314 3.5 19.4V9.6C3.5 9.26863 3.76863 9 4.1 9H6.86762C7.57015 9 8.22116 8.6314 8.5826 8.02899L11.293 3.51161C11.8779 2.53688 13.2554 2.44422 13.9655 3.33186C14.3002 3.75025 14.4081 4.30635 14.2541 4.81956L13.2317 8.22759C13.1162 8.61256 13.4045 9 13.8064 9H18.3815C19.7002 9 20.658 10.254 20.311 11.5262L18.4019 18.5262C18.1646 19.3964 17.3743 20 16.4724 20Z" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"></path>
                                    <path d="M7 20L7 9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"></path>
                                  </svg>
                                </button>
                                <button
                                  className={`feedback-btn ${messageFeedback[idx] === 'dislike' ? 'active-dislike' : ''}`}
                                  onClick={() => handleMessageFeedback(idx, 'dislike')}
                                  title="Not helpful"
                                >
                                  <svg width="20" height="20" strokeWidth="1.4" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M16.4724 3.5H4.1C3.76863 3.5 3.5 3.76863 3.5 4.1V13.9C3.5 14.2314 3.76863 14.5 4.1 14.5H6.86762C7.57015 14.5 8.22116 14.8686 8.5826 15.471L11.293 19.9884C11.8779 20.9631 13.2554 21.0558 13.9655 20.1681C14.3002 19.7497 14.4081 19.1937 14.2541 18.6804L13.2317 15.2724C13.1162 14.8874 13.4045 14.5 13.8064 14.5H18.3815C19.7002 14.5 20.658 13.246 20.311 11.9738L18.4019 4.97376C18.1646 4.10364 17.3743 3.5 16.4724 3.5Z" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"></path>
                                    <path d="M7 14.5L7 3.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"></path>
                                  </svg>
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                    </div>
                  </div>
                    );
                  })}
                {isThinking && (
                    <div className="message assistant">
                      <div className="avatar">
                        <img src="/avatar.svg" alt="AI" />
                    </div>
                      <div className="message-bubble assistant thinking">
                        <span className="thinking-text">thinking</span>
                        <span className="thinking-dots">
                          <span className="dot">.</span>
                          <span className="dot">.</span>
                          <span className="dot">.</span>
                        </span>
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>
                <div className="input-container">
                  <textarea
                    className="message-input"
                    placeholder="Ask me anything..."
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    rows={1}
                    disabled={isThinking}
                  />
                  <button className="send-btn" onClick={handleSend} disabled={isThinking || !message.trim()}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path d="M22.1525 3.55321L11.1772 21.0044L9.50686 12.4078L2.00002 7.89795L22.1525 3.55321Z" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
                      <path d="M9.45557 12.4436L22.1524 3.55321" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
                    </svg>
                  </button>
                    </div>
                  </div>
                )}
          </main>
        </div>
      ) : (
        <div className="dashboard-content">
          {userData && userData.user_type === 'medical_student' ? (
            <>
              <div className="dashboard-banner" onClick={() => handleMenuClick('ai-chat')}>
                <img src="/images/student-banner.svg" alt="Banner" className="banner-image" />
              </div>
              <div className="dashboard-cards-grid">
                {/* Question Bank Card */}
                <div className="feature-card qb-card" onClick={() => navigate('/question-bank')}>
                  <div className="feature-card-header">
                    <div className="feature-icon qb-icon">
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M2 3H8C9.06087 3 10.0783 3.42143 10.8284 4.17157C11.5786 4.92172 12 5.93913 12 7V21C12 20.2044 11.6839 19.4413 11.1213 18.8787C10.5587 18.3161 9.79565 18 9 18H2V3Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M22 3H16C14.9391 3 13.9217 3.42143 13.1716 4.17157C12.4214 4.92172 12 5.93913 12 7V21C12 20.2044 12.3161 19.4413 12.8787 18.8787C13.4413 18.3161 14.2044 18 15 18H22V3Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <div className="feature-arrow">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M7 17L17 7M17 7H7M17 7V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                  </div>
                  <div className="feature-card-content">
                    <h3 className="feature-title">Question Bank</h3>
                    <p className="feature-description">Practice MCQs from your textbooks and track your progress</p>
                  </div>
                </div>

                {/* Exams Card */}
                <div className="feature-card exam-card" onClick={() => navigate('/exams')}>
                  <div className="feature-card-header">
                    <div className="feature-icon exam-icon">
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M9 5H7C5.89543 5 5 5.89543 5 7V19C5 20.1046 5.89543 21 7 21H17C18.1046 21 19 20.1046 19 19V7C19 5.89543 18.1046 5 17 5H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M9 5C9 3.89543 9.89543 3 11 3H13C14.1046 3 15 3.89543 15 5C15 6.10457 14.1046 7 13 7H11C9.89543 7 9 6.10457 9 5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M9 12H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        <path d="M9 16H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                    </div>
                    <div className="feature-arrow">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M7 17L17 7M17 7H7M17 7V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                  </div>
                  <div className="feature-card-content">
                    <h3 className="feature-title">Exams</h3>
                    <p className="feature-description">Take timed tests and get detailed performance analytics</p>
                  </div>
                </div>

                {/* Feedback Card */}
                <div className="feature-card feedback-card" onClick={() => navigate('/feedback')}>
                  <div className="feature-card-header">
                    <div className="feature-icon feedback-icon">
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M21 11.5C21.0034 12.8199 20.6951 14.1219 20.1 15.3C19.3944 16.7118 18.3098 17.8992 16.9674 18.7293C15.6251 19.5594 14.0782 19.9994 12.5 20C11.1801 20.0035 9.87812 19.6951 8.7 19.1L3 21L4.9 15.3C4.30493 14.1219 3.99656 12.8199 4 11.5C4.00061 9.92179 4.44061 8.37488 5.27072 7.03258C6.10083 5.69028 7.28825 4.6056 8.7 3.90003C9.87812 3.30496 11.1801 2.99659 12.5 3.00003H13C15.0843 3.11502 17.053 3.99479 18.5291 5.47089C20.0052 6.94699 20.885 8.91568 21 11V11.5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <div className="feature-arrow">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M7 17L17 7M17 7H7M17 7V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                  </div>
                  <div className="feature-card-content">
                    <h3 className="feature-title">Feedback</h3>
                    <p className="feature-description">Share your thoughts and help us improve the platform</p>
                  </div>
                </div>
              </div>
            </>
          ) : userData && userData.user_type === 'doctor' ? (
            <>
              <div className="dashboard-banner" onClick={() => handleMenuClick('ai-chat')}>
                <img src="/images/student-banner.svg" alt="Banner" className="banner-image" />
              </div>
              <div className="dashboard-cards-grid">
                {/* Medical White Paper Card */}
                <div className="dashboard-card step-card-style">
                  <div className="card-number-badge">1</div>
                  <div style={{ position: 'absolute', top: '1rem', right: '1rem', background: '#6d56ff', color: 'white', padding: '0.25rem 0.75rem', borderRadius: '12px', fontSize: '0.75rem', fontWeight: '600' }}>Coming Soon</div>
                  <div className="card-visual">
                    <div className="card-visual-content">
                      <div className="practice-icon">
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M4 19V5C4 3.89543 4.89543 3 6 3H19.4C19.7314 3 20 3.26863 20 3.6V16.7143" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                          <path d="M6 17L20 17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                          <path d="M6 21L20 21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                          <path d="M6 21C4.89543 21 4 20.1046 4 19C4 17.8954 4.89543 17 6 17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M9 7L15 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                        </svg>
                      </div>
                      <div className="card-visual-dots">
                        <div className="visual-dot"></div>
                        <div className="visual-dot"></div>
                        <div className="visual-dot"></div>
                      </div>
                    </div>
                  </div>
                  <h3 className="card-title-step">Medical White Paper</h3>
                  <p className="card-description-step">Access comprehensive medical white papers and research documents. Stay updated with the latest medical research and evidence-based practices.</p>
                  <button className="card-primary-btn-step" disabled style={{ opacity: 0.6, cursor: 'not-allowed' }}>Coming Soon</button>
                </div>

                {/* Cheat Sheet Card */}
                <div className="dashboard-card step-card-style">
                  <div className="card-number-badge">2</div>
                  <div style={{ position: 'absolute', top: '1rem', right: '1rem', background: '#6d56ff', color: 'white', padding: '0.25rem 0.75rem', borderRadius: '12px', fontSize: '0.75rem', fontWeight: '600' }}>Coming Soon</div>
                  <div className="card-visual">
                    <div className="card-visual-content">
                      <div className="cheat-sheet-icon">
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                      <div className="card-visual-grid">
                        <div className="grid-item"></div>
                        <div className="grid-item"></div>
                        <div className="grid-item"></div>
                        <div className="grid-item"></div>
                      </div>
                    </div>
                  </div>
                  <h3 className="card-title-step">Cheat Sheets</h3>
                  <p className="card-description-step">Get helpful tips and tricks on how to use the tool better. Learn best practices, shortcuts, and strategies to maximize your workflow efficiency.</p>
                  <button className="card-primary-btn-step" disabled style={{ opacity: 0.6, cursor: 'not-allowed' }}>Coming Soon</button>
                </div>
              </div>
            </>
          ) : (
            <div className="dashboard-content">
              <h2>Welcome to S.C.A.L.P.E.L.</h2>
              <p>Hello, {userData?.fullName || userData?.full_name || 'User'}!</p>
                  </div>
                )}
              </div>
      )}

      {/* Mobile Bottom Menu */}
      {!isChatPage && (
        <nav className="dashboard-nav mobile-nav">
          {menuItems.map((item) => (
            <button
              key={item.id}
              className={`nav-menu-item ${activeMenu === item.id ? 'active' : ''}`}
              onClick={() => handleMenuClick(item.id)}
            >
              <span className="nav-menu-icon">{item.icon}</span>
              <span className="nav-menu-label">{item.label}</span>
            </button>
          ))}
        </nav>
      )}

      {/* Book Filter Popup */}
      {showBookFilterPopup && (
        <div className="book-filter-popup-overlay" onClick={() => setShowBookFilterPopup(false)}>
          <div className="book-filter-popup" onClick={(e) => e.stopPropagation()}>
            <div className="book-filter-popup-header">
              <h2 className="book-filter-popup-title">Select Books</h2>
              <button 
                className="book-filter-popup-close"
                onClick={() => setShowBookFilterPopup(false)}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
              </button>
                  </div>
            
            <div className="book-filter-popup-search">
                  <input
                    type="text"
                placeholder="Search books by name or author..."
                value={bookSearchQuery}
                onChange={(e) => handleBookSearch(e.target.value)}
                className="book-filter-search-input"
              />
            </div>

            <div className="book-filter-popup-content">
              {isLoadingBooks ? (
                <div className="book-filter-loading">Loading books...</div>
              ) : filteredBooks.length === 0 ? (
                <div className="book-filter-empty">No books found</div>
              ) : (
                <div className="book-filter-list">
                  {filteredBooks.map((book) => (
                    <label 
                      key={book.id} 
                      className={`book-filter-item ${selectedBooks.includes(book.id) ? 'selected' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedBooks.includes(book.id)}
                        onChange={() => handleBookToggle(book.id)}
                        className="book-filter-checkbox"
                      />
                      <div className="book-filter-item-content">
                        <div className="book-filter-item-name">{book.name}</div>
                        {book.author && (
                          <div className="book-filter-item-author">by {book.author}</div>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>

            <div className="book-filter-popup-footer">
              <button 
                className="book-filter-clear-btn"
                onClick={handleClearAllBooks}
                disabled={selectedBooks.length === 0}
              >
                Clear All
              </button>
              <button 
                className="book-filter-apply-btn"
                onClick={handleApplyBookFilter}
              >
                Apply Filter
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Exam Filter Popup */}
      {showExamFilterPopup && (
        <div className="book-filter-popup-overlay" onClick={() => setShowExamFilterPopup(false)}>
          <div className="book-filter-popup" onClick={(e) => e.stopPropagation()}>
            <div className="book-filter-popup-header">
              <h2 className="book-filter-popup-title">Select Exams</h2>
              <button 
                className="book-filter-popup-close"
                onClick={() => setShowExamFilterPopup(false)}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                    </button>
                  </div>
            
            <div className="book-filter-popup-search">
              <input
                type="text"
                placeholder="Search exams by name..."
                value={examSearchQuery}
                onChange={(e) => handleExamSearch(e.target.value)}
                className="book-filter-search-input"
              />
                </div>

            <div className="book-filter-popup-content">
              {isLoadingExams ? (
                <div className="book-filter-loading">Loading exams...</div>
              ) : filteredExams.length === 0 ? (
                <div className="book-filter-empty">No exams found</div>
              ) : (
                <div className="book-filter-list">
                  {filteredExams.map((exam) => (
                    <label 
                      key={exam.id} 
                      className={`book-filter-item ${selectedExams.includes(exam.id) ? 'selected' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedExams.includes(exam.id)}
                        onChange={() => handleExamToggle(exam.id)}
                        className="book-filter-checkbox"
                      />
                      <div className="book-filter-item-content">
                        <div className="book-filter-item-name">{exam.name}</div>
              </div>
                    </label>
                  ))}
                </div>
          )}
      </div>

            <div className="book-filter-popup-footer">
              <button 
                className="book-filter-clear-btn"
                onClick={handleClearAllExams}
                disabled={selectedExams.length === 0}
              >
                Clear All
              </button>
              <button 
                className="book-filter-apply-btn"
                onClick={handleApplyExamFilter}
              >
                Apply Filter
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Subject Filter Popup */}
      {showSubjectFilterPopup && (
        <div className="book-filter-popup-overlay" onClick={() => setShowSubjectFilterPopup(false)}>
          <div className="book-filter-popup" onClick={(e) => e.stopPropagation()}>
            <div className="book-filter-popup-header">
              <h2 className="book-filter-popup-title">Select Subjects</h2>
            <button
                className="book-filter-popup-close"
                onClick={() => setShowSubjectFilterPopup(false)}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
            </button>
          </div>
            
            <div className="book-filter-popup-search">
              <input
                type="text"
                placeholder="Search subjects by name..."
                value={subjectSearchQuery}
                onChange={(e) => handleSubjectSearch(e.target.value)}
                className="book-filter-search-input"
              />
            </div>

            <div className="book-filter-popup-content">
              {isLoadingSubSpecialities ? (
                <div className="book-filter-loading">Loading subjects...</div>
              ) : filteredSubSpecialities.length === 0 ? (
                <div className="book-filter-empty">No subjects found</div>
              ) : (
                <div className="book-filter-list">
                  {filteredSubSpecialities.map((sub) => (
                    <label 
                      key={sub.id} 
                      className={`book-filter-item ${selectedSubSpecialities.includes(sub.id) ? 'selected' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedSubSpecialities.includes(sub.id)}
                        onChange={() => handleSubjectToggle(sub.id)}
                        className="book-filter-checkbox"
                      />
                      <div className="book-filter-item-content">
                        <div className="book-filter-item-name">{sub.name}</div>
                      </div>
                    </label>
                  ))}
        </div>
      )}
            </div>

            <div className="book-filter-popup-footer">
              <button
                className="book-filter-clear-btn"
                onClick={handleClearAllSubjects}
                disabled={selectedSubSpecialities.length === 0}
              >
                Clear All
              </button>
              <button
                className="book-filter-apply-btn"
                onClick={handleApplySubjectFilter}
              >
                Apply Filter
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Citation Text Tooltip Modal */}
      {showCitationTooltip && tooltipContent && (
        <div
          className="citation-tooltip-overlay"
          onClick={() => setShowCitationTooltip(false)}
        >
          <div
            className="citation-tooltip-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="citation-tooltip-header">
              <div className="citation-tooltip-title">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 3H5C3.89543 3 3 3.89543 3 5V19C3 20.1046 3.89543 21 5 21H9M15 3H19C20.1046 3 21 3.89543 21 5V19C21 20.1046 20.1046 21 19 21H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M9 12H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  <path d="M9 7H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  <path d="M9 17H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                Source Text
              </div>
              <button
                className="citation-tooltip-close"
                onClick={() => setShowCitationTooltip(false)}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
            <div className="citation-tooltip-meta">
              <strong>{tooltipContent.book_title}</strong>
              {tooltipContent.book_author && <span> by {tooltipContent.book_author}</span>}
            </div>
            <div className="citation-tooltip-text">
              {tooltipContent.text}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Dashboard;
